/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useState,
  useImperativeHandle,
  useRef,
  type ComponentProps,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import isEmail from 'validator/lib/isEmail';
import Field from './Field/Field';

const DEFAULT_FORWARDED_REF: RefObject<FormHandle | null> = { current: null };
const DEFAULT_FIELDS: FormField[] = [];

const DEFAULT_ERRORS: Record<string, string> = {
  required: 'Required {field}',
  invalid: 'Invalid {field}',
};

const DEFAULT_ON_CHANGE = () => {};
const DEFAULT_ON_FOCUS = () => {};
const DEFAULT_ON_BLUR = () => {};
const DEFAULT_ON_SUBMIT = () => {};
const DEFAULT_ON_ERROR = () => {};

function getDefaultState(fields: FormField[]) {
  const state: FormState = {};
  fields.forEach(field => {
    if (typeof field.value === 'undefined') {
      if (field.type === 'choices' && field.multiple) {
        state[field.id] = [];
      } else {
        state[field.id] = '';
      }
    } else if (Array.isArray(field.value)) {
      state[field.id] = [...field.value];
    } else if (typeof field.value === 'object') {
      state[field.id] = { ...(field.value as object) } as unknown as FormValue;
    } else {
      state[field.id] = field.value;
    }
  });

  return state;
}

function getDefaultErrors(fields: FormField[]) {
  const errors: Record<string, string | null> = {};
  fields.forEach(field => {
    errors[field.id] = null;
  });

  return errors;
}

type FormValue = ComponentProps<typeof Field>['value'];

export type FormState = Record<string, FormValue>;

/** A field as the form is given it; the form adds `error`, and takes over `onChange`. */
export type FormField = Omit<
  ComponentProps<typeof Field>,
  'error' | 'onChange'
> & {
  /** Messages for this field, over the form's `errors`. */
  messages?: Record<string, string>;
};

export interface FormHandle {
  focus: () => void;
  reset: () => void;
}

export interface FormProps {
  forwardedRef?: Ref<FormHandle>;
  className?: string;
  fields?: FormField[];
  errors?: Record<string, string>;
  required?: string | null;
  children?: ReactNode;
  submit?: string;
  disabled?: boolean;
  inactive?: boolean;
  onChange?: (field: FormField) => void;
  onFocus?: (field: FormField) => void;
  onBlur?: (field: FormField) => void;
  onSubmit?: (state: FormState) => void;
  onError?: () => void;
}

/**
 * This is the Form component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Form({
  forwardedRef = DEFAULT_FORWARDED_REF,
  className,
  fields = DEFAULT_FIELDS,
  errors: messages = DEFAULT_ERRORS,
  required = null,
  children,
  submit = 'Submit',
  disabled = false,
  inactive = false,
  onChange = DEFAULT_ON_CHANGE,
  onFocus = DEFAULT_ON_FOCUS,
  onBlur = DEFAULT_ON_BLUR,
  onSubmit = DEFAULT_ON_SUBMIT,
  onError = DEFAULT_ON_ERROR,
}: FormProps) {
  const ref = useRef<HTMLFormElement>(null);
  const [state, setState] = useState(getDefaultState(fields));
  const [errors, setErrors] = useState(getDefaultErrors(fields));

  function validateField({ id, type, multiple, required }: FormField) {
    let error: string | null = null;
    const value = state[id];

    if (required) {
      if (type === 'choices' && multiple) {
        if ((value as unknown[]).length === 0) {
          error = 'required';
        }
      } else if (!value) {
        error = 'required';
      }
    }

    if (!error && type === 'email') {
      if (!isEmail(value as string)) {
        error = 'invalid';
      }
    }

    if (!error && type === 'choices' && multiple) {
      if (!Array.isArray(value)) {
        error = 'invalid';
      }
    }

    setErrors(prev => ({
      ...prev,
      [id]: error,
    }));

    return !error;
  }

  function validate() {
    let valid = true;

    fields.forEach(field => {
      if (!validateField(field)) {
        valid = false;
      }
    });

    return valid;
  }

  function getError({ id, label, messages: fieldMessages = {} }: FormField) {
    if (!errors[id]) return null;
    const key = errors[id];
    const componentMessages = {
      ...messages,
      ...fieldMessages,
    };

    if (componentMessages[key]) {
      return componentMessages[key].replace('{field}', label.toLowerCase());
    }
    return key;
  }

  useImperativeHandle(forwardedRef, () => ({
    focus: () => {
      const firstField = ref.current!.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea'
      );
      if (firstField) {
        firstField.focus();
      }
    },
    reset: () => {
      setState(getDefaultState(fields));
    },
  }));

  function renderRequiredTip() {
    if (!required) return null;

    const hasRequiredField = fields.reduce((prev, current) => {
      if (current.required) {
        return true;
      }
      return prev;
    }, false);

    if (!hasRequiredField) return null;

    return (
      <small
        className="required-tip"
        dangerouslySetInnerHTML={{ __html: required }}
      />
    );
  }

  return (
    <form
      ref={ref}
      className={className}
      // @ts-expect-error `disabled` is not a form attribute, but React renders it. Kept as before the conversion.
      disabled={disabled}
      noValidate
      onSubmit={e => {
        e.preventDefault();
        if (validate()) {
          onSubmit(state);
        } else {
          // Not process.nextTick: webpack 5 no longer provides `process` in the browser.
          setTimeout(onError, 0);
        }
      }}
    >
      {renderRequiredTip()}
      {fields.map(({ id, ...props }) => (
        <Field
          key={id}
          id={id}
          {...props}
          value={state[id]}
          disabled={disabled}
          tabIndex={inactive ? -1 : undefined}
          error={getError({ id, ...props })}
          onChange={({ target: { value } }) => {
            setState(state => ({
              ...state,
              [id]: value,
            }));
            setErrors(state => ({
              ...state,
              [id]: null,
            }));
            onChange({
              id,
              ...props,
              value,
            });
          }}
          onFocus={() => {
            setErrors(state => ({
              ...state,
              [id]: null,
            }));
            onFocus({
              id,
              ...props,
            });
          }}
          onBlur={() => {
            validateField({ id, ...props });
            onBlur({
              id,
              ...props,
            });
          }}
        />
      ))}
      {children}
      <button
        type="submit"
        className="ticket-button"
        tabIndex={inactive ? -1 : undefined}
      >
        <span>{submit}</span>
      </button>
    </form>
  );
}

export default React.forwardRef<FormHandle, Omit<FormProps, 'forwardedRef'>>(
  (props, ref) => <Form {...props} forwardedRef={ref} />
);
